import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import QRCode from '../../components/QRCode';
import SEOHead from '../../components/SEOHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const servicesSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Pet Care Services - Fluffy Pets',
  url: `${SITE_URL}/services`,
  description:
    'Comprehensive pet care services: professional grooming, boarding, dog walking, veterinary care, training classes, and spa wellness — all under one roof in San Francisco.',
  isPartOf: { '@type': 'WebSite', name: 'Fluffy Pets', url: SITE_URL },
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Professional Grooming', description: 'Full bath, breed-specific cuts, nail trimming, ear cleaning and teeth brushing from $45.' },
      { '@type': 'ListItem', position: 2, name: 'Pet Boarding', description: 'Climate-controlled rooms with 24/7 supervision from $35/night.' },
      { '@type': 'ListItem', position: 3, name: 'Dog Walking', description: '30 or 60-minute walks with GPS tracking from $20/walk.' },
      { '@type': 'ListItem', position: 4, name: 'Veterinary Care', description: 'Comprehensive health services including wellness exams, vaccinations, dental care and surgery.' },
      { '@type': 'ListItem', position: 5, name: 'Training Classes', description: 'Puppy socialization through advanced training from $150/course.' },
      { '@type': 'ListItem', position: 6, name: 'Pet Spa & Wellness', description: 'Therapeutic massage, aromatherapy, and paw treatments from $55.' },
      { '@type': 'ListItem', position: 7, name: 'Pet Food', description: 'Premium dry & wet food, grain-free options and organic brands from $15.' },
      { '@type': 'ListItem', position: 8, name: 'Pet Accessories', description: 'Toys, beds, collars, leashes, bowls and carriers from $10.' },
    ],
  },
};

// ... existing code ...

export default function ServicesPage() {
  const services = [
    {
      icon: 'ri-scissors-cut-line',
      title: 'Professional Grooming',
      description: 'Complete grooming services including bathing, haircuts, nail trimming, and ear cleaning. Our experienced groomers handle pets with care and patience.',
      features: ['Full Bath & Blow Dry', 'Breed-Specific Cuts', 'Nail Trimming & Filing', 'Ear Cleaning', 'Teeth Brushing'],
      price: 'From $45'
    },
    {
      icon: 'ri-hotel-bed-line',
      title: 'Pet Boarding',
      description: 'Safe and comfortable boarding facilities with 24/7 supervision. Your pets will enjoy spacious accommodations and plenty of playtime.',
      features: ['Climate-Controlled Rooms', '24/7 Supervision', 'Daily Exercise & Play', 'Medication Administration', 'Regular Updates'],
      price: 'From $35/night'
    },
    {
      icon: 'ri-walk-line',
      title: 'Dog Walking',
      description: 'Regular exercise and outdoor adventures for your furry friends. Professional walkers ensure safe and enjoyable walks.',
      features: ['30 or 60 Minute Walks', 'Small Group or Solo', 'GPS Tracking', 'Photo Updates', 'Flexible Scheduling'],
      price: 'From $20/walk'
    },
    {
      icon: 'ri-stethoscope-line',
      title: 'Veterinary Care',
      description: 'Comprehensive health services from routine check-ups to emergency care. Our licensed veterinarians provide expert medical attention.',
      features: ['Wellness Exams', 'Vaccinations', 'Dental Care', 'Surgery Services', 'Emergency Care'],
      price: 'Varies by service'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Training Classes',
      description: 'Professional training programs for puppies and adult dogs. Build better behavior and strengthen your bond with your pet.',
      features: ['Puppy Socialization', 'Basic Obedience', 'Advanced Training', 'Behavior Modification', 'Private Sessions Available'],
      price: 'From $150/course'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Pet Spa & Wellness',
      description: 'Luxurious spa treatments and wellness services to pamper your pets. From massages to aromatherapy, we offer it all.',
      features: ['Therapeutic Massage', 'Aromatherapy', 'Paw Treatment', 'Coat Conditioning', 'Relaxation Sessions'],
      price: 'From $55'
    },
    {
      icon: 'ri-restaurant-2-line',
      title: 'Pet Food',
      description: 'Premium quality pet food and nutrition products for dogs, cats, and other pets. We offer a wide selection of brands and specialized diets.',
      features: ['Premium Dry & Wet Food', 'Grain-Free Options', 'Special Diet Foods', 'Organic & Natural Brands', 'Treats & Supplements'],
      price: 'From $15'
    },
    {
      icon: 'ri-shopping-bag-3-line',
      title: 'Pet Accessories',
      description: 'Everything your pet needs from toys to beds, collars to carriers. Quality accessories to keep your pets comfortable and happy.',
      features: ['Toys & Entertainment', 'Beds & Blankets', 'Collars & Leashes', 'Bowls & Feeders', 'Carriers & Crates'],
      price: 'From $10'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Pet Care Services San Francisco | Grooming, Boarding, Veterinary - Fluffy Pets"
        description="Explore Fluffy Pets' full range of services: professional grooming from $45, overnight boarding from $35, veterinary care, dog walking, training, and spa treatments in San Francisco."
        keywords="pet grooming San Francisco, pet boarding, dog walking, veterinary care, pet training, pet spa, pet accessories"
        canonical={`${SITE_URL}/services`}
        schema={servicesSchema}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-purple-300/30 to-pink-300/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-tr from-pink-300/30 to-purple-300/30 rounded-full blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Our Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Comprehensive pet care services designed to keep your furry friends happy, healthy, and looking their best. From grooming to veterinary care, we've got everything your pet needs.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-purple-200">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${service.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-800">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3">What&apos;s Included:</h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-gray-600">
                        <i className="ri-check-line text-purple-500 mt-1 mr-2 flex-shrink-0"></i>
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                  <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    {service.price}
                  </span>
                  <a href="/booking" className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-semibold hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer">
                    Book Now
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Why Choose Fluffy Pets?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're committed to providing the highest quality care for your beloved pets
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'ri-shield-check-line', title: 'Licensed & Insured', desc: 'Fully certified professionals with comprehensive insurance coverage' },
              { icon: 'ri-time-line', title: 'Flexible Hours', desc: 'Open 7 days a week with extended hours for your convenience' },
              { icon: 'ri-heart-3-line', title: 'Passionate Team', desc: 'Animal lovers dedicated to providing exceptional care' },
              { icon: 'ri-customer-service-2-line', title: '24/7 Support', desc: 'Always available for emergencies and questions' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mx-auto mb-6">
                  <i className={`${item.icon} text-4xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-800">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QR Code Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-12 text-center">
            <div className="mb-8">
              <h2 className="text-4xl font-black mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Share Our Services
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Scan this QR code to quickly access our services page on your mobile device or share it with friends and family!
              </p>
            </div>
            <div className="flex justify-center">
              <QRCode url={`${window.location.origin}/services`} size={220} showDownload={true} />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Ready to Book a Service?
          </h2>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed">
            Give your pet the care they deserve. Schedule an appointment today and experience the Fluffy Pets difference!
          </p>
          <a href="/booking" className="inline-block px-10 py-5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-lg font-bold rounded-full hover:shadow-2xl transition-all duration-300 whitespace-nowrap cursor-pointer">
            Book an Appointment
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
